<template>
  <div class="pdm-wrap">
    <!-- ══ HEADER ══ -->
    <div class="pdm-header">
      <div class="pdm-header-left">
        <div class="pdm-icon-wrap">
          <v-icon size="20" color="white">mdi-map-marker-radius</v-icon>
        </div>
        <div>
          <div class="pdm-title">ความหนาแน่นกรอบอัตรากำลัง (จอร.18) — รายจังหวัด</div>
          <div class="pdm-subtitle">สำรวจการกระจายตัวของตำแหน่ง ครองแล้ว/ว่าง และเทียบกับเกณฑ์อัตรากำลัง</div>
        </div>
      </div>
      <div class="pdm-header-right">
        <v-chip x-small outlined color="#e4e4e7" class="mr-2" style="color:#64748b" v-if="criterionYear">
          <v-icon size="11" left>mdi-calendar</v-icon>เกณฑ์ปี {{ criterionYear }}
        </v-chip>
      </div>
    </div>

    <!-- ══ CONTROLS ══ -->
    <div class="pdm-controls">
      <!-- Position type pills -->
      <div class="pdm-filter-group pdm-filter-group--grow">
        <div class="pdm-filter-label">ประเภทตำแหน่ง</div>
        <div class="pdm-pill-group pdm-pill-group--wrap">
          <span
            v-for="t in positionTypeOptions" :key="t.value"
            :class="['pdm-pill', { 'pdm-pill--active': selectedType === t.value }]"
            @click="selectType(t.value)"
          >{{ t.value }} ({{ formatNum(t.total) }})</span>
        </div>
      </div>

      <!-- Metric mode -->
      <div class="pdm-filter-group">
        <div class="pdm-filter-label">มุมมอง</div>
        <div class="pdm-pill-group">
          <span
            :class="['pdm-pill', { 'pdm-pill--active': metric === 'density' }]"
            @click="metric = 'density'"
          >ความหนาแน่น</span>
          <span
            v-if="selectedHasCriterion"
            :class="['pdm-pill', { 'pdm-pill--active': metric === 'criterion' }]"
            @click="metric = 'criterion'"
          >ขาด/เกินเกณฑ์</span>
        </div>
      </div>

      <!-- Color scheme (density mode only) -->
      <div class="pdm-filter-group" v-if="metric === 'density'">
        <div class="pdm-filter-label">สี</div>
        <div class="pdm-pill-group">
          <span
            v-for="s in schemeOptions" :key="s.value"
            :class="['pdm-pill', { 'pdm-pill--active': colorScheme === s.value }]"
            @click="colorScheme = s.value"
          >
            <span class="pdm-scheme-dot" :style="`background:${s.color}`"></span>
            {{ s.label }}
          </span>
        </div>
      </div>
    </div>

    <div class="pdm-no-criterion-note" v-if="metric === 'criterion' && !selectedHasCriterion">
      ตำแหน่งประเภทนี้ยังไม่มีเกณฑ์อัตรากำลังมาตรฐานในระบบ (มีเฉพาะ ผู้อำนวยการ / รองผู้อำนวยการ / ครู)
    </div>

    <!-- ══ MAIN CONTENT ══ -->
    <div class="pdm-body">
      <!-- LEFT: MAP -->
      <div class="pdm-map-panel">
        <!-- Map title bar -->
        <div class="pdm-map-title-bar">
          <span class="pdm-map-type-name">{{ selectedType }}</span>
          <span class="pdm-map-stats">
            <template v-if="metric === 'density'">
              รวม {{ formatNum(typeTotal) }} ตำแหน่ง · ครอง {{ formatNum(typeOccupied) }} · ว่าง {{ formatNum(typeVacant) }}
            </template>
            <template v-else-if="selectedHasCriterion">
              อัตรากำลังจริง {{ formatNum(criterionManpowerTotal) }} · เกณฑ์ {{ formatNum(criterionQuotaTotal) }} ·
              {{ criterionIncompleteTotal < 0 ? 'ขาด' : 'เกิน' }} {{ formatNum(Math.abs(criterionIncompleteTotal)) }}
            </template>
          </span>
          <div class="pdm-zoom-btns">
            <button class="pdm-zoom-btn" @click="mapZoomIn">+</button>
            <button class="pdm-zoom-btn" @click="mapZoomOut">−</button>
            <button class="pdm-zoom-btn" @click="mapFit">⊡</button>
          </div>
        </div>

        <!-- Leaflet map -->
        <div class="pdm-leaflet-container" ref="mapEl"></div>

        <!-- Legend -->
        <div class="pdm-legend">
          <div class="pdm-legend-title">{{ legendTitle }}</div>
          <div class="pdm-legend-bar">
            <div class="pdm-legend-gradient" :style="legendGradient"></div>
            <div class="pdm-legend-ticks">
              <span>{{ legendMinLabel }}</span>
              <span>{{ legendMaxLabel }}</span>
            </div>
          </div>
        </div>

        <!-- Tooltip -->
        <div
          class="pdm-tooltip"
          v-if="tooltip.show"
          :style="`left:${tooltip.x}px;top:${tooltip.y}px`"
        >
          <div class="pdm-tt-header">
            <span class="pdm-tt-prov">{{ tooltip.province }}</span>
            <span class="pdm-tt-region" :style="`background:${tooltip.regionColor}`">{{ tooltip.region }}</span>
          </div>
          <div class="pdm-tt-body">
            <template v-if="metric === 'density'">
              <div class="pdm-tt-row"><span>ตำแหน่งทั้งหมด</span><strong>{{ formatNum(tooltip.total) }}</strong></div>
              <div class="pdm-tt-row"><span>ครองแล้ว</span><strong class="tc-blue">{{ formatNum(tooltip.occupied) }}</strong></div>
              <div class="pdm-tt-row"><span>ว่าง</span><strong class="tc-orange">{{ formatNum(tooltip.vacant) }}</strong></div>
              <div class="pdm-tt-row"><span>สถานศึกษา</span><strong>{{ tooltip.colleges }} แห่ง</strong></div>
            </template>
            <template v-else>
              <div class="pdm-tt-row"><span>อัตรากำลังจริง</span><strong>{{ formatNum(tooltip.manpower) }}</strong></div>
              <div class="pdm-tt-row"><span>เกณฑ์</span><strong>{{ formatNum(tooltip.criterion) }}</strong></div>
              <div class="pdm-tt-row">
                <span>ขาด / เกิน</span>
                <strong :class="tooltip.incomplete < 0 ? 'tc-red' : 'tc-green'">
                  {{ tooltip.incomplete < 0 ? '▼' : '▲' }}{{ formatNum(Math.abs(tooltip.incomplete)) }}
                </strong>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- RIGHT: STATS PANEL -->
      <div class="pdm-stats-panel">
        <!-- Summary cards -->
        <div class="pdm-stat-cards">
          <template v-if="metric === 'density'">
            <div class="pdm-stat-card pdm-stat-card--blue">
              <div class="pdm-stat-val">{{ formatNum(typeOccupied) }}</div>
              <div class="pdm-stat-lbl">ครองแล้ว</div>
            </div>
            <div class="pdm-stat-card pdm-stat-card--orange">
              <div class="pdm-stat-val">{{ formatNum(typeVacant) }}</div>
              <div class="pdm-stat-lbl">ว่าง</div>
            </div>
          </template>
          <template v-else>
            <div class="pdm-stat-card" :class="criterionIncompleteTotal < 0 ? 'pdm-stat-card--red' : 'pdm-stat-card--teal'">
              <div class="pdm-stat-val">{{ criterionIncompleteTotal < 0 ? '▼' : '▲' }}{{ formatNum(Math.abs(criterionIncompleteTotal)) }}</div>
              <div class="pdm-stat-lbl">{{ criterionIncompleteTotal < 0 ? 'ขาดเกณฑ์รวม' : 'เกินเกณฑ์รวม' }}</div>
            </div>
            <div class="pdm-stat-card pdm-stat-card--purple">
              <div class="pdm-stat-val">{{ formatNum(criterionQuotaTotal) }}</div>
              <div class="pdm-stat-lbl">เกณฑ์รวม</div>
            </div>
          </template>
        </div>

        <!-- Province ranking table -->
        <div class="pdm-rank-panel" v-if="provinceRanking.length">
          <div class="pdm-rank-header">
            <v-icon size="14" color="#455a64" class="mr-1">mdi-podium</v-icon>
            <span>{{ metric === 'density' ? 'อันดับจังหวัด (ตำแหน่งทั้งหมด)' : 'อันดับจังหวัด (ขาดเกณฑ์มากสุด)' }}</span>
            <v-spacer />
            <span class="pdm-rank-count">{{ provinceRanking.length }} จังหวัด</span>
          </div>
          <div class="pdm-rank-search">
            <input v-model="rankSearch" class="pdm-input pdm-input--sm" placeholder="กรองจังหวัด..." />
          </div>
          <div class="pdm-rank-list">
            <div
              v-for="(p, i) in filteredRanking" :key="p.name"
              class="pdm-rank-row"
              :class="{ 'pdm-rank-row--selected': selectedProvince === p.name }"
              @click="onClickProvByName(p.name)"
            >
              <div class="pdm-rank-num" :class="getRankClass(i)">{{ i + 1 }}</div>
              <div class="pdm-rank-info">
                <div class="pdm-rank-name">{{ p.name }}</div>
                <div class="pdm-rank-region" :style="`color:${getRegionColor(p.region)}`">{{ p.region }}</div>
              </div>
              <div class="pdm-rank-val" v-if="metric === 'density'">
                <span class="tc-blue">{{ formatNum(p.occupied) }}</span> /
                <span class="tc-orange">{{ formatNum(p.vacant) }}</span>
              </div>
              <div class="pdm-rank-val" v-else>
                <strong :class="p.incomplete < 0 ? 'tc-red' : 'tc-green'">
                  {{ p.incomplete < 0 ? '▼' : '▲' }}{{ formatNum(Math.abs(p.incomplete)) }}
                </strong>
              </div>
            </div>
          </div>
        </div>

        <!-- Region breakdown -->
        <div class="pdm-region-panel" v-if="regionBreakdown.length">
          <div class="pdm-rank-header">
            <v-icon size="14" color="#455a64" class="mr-1">mdi-map-legend</v-icon>
            <span>สรุปรายภาค</span>
          </div>
          <div class="pdm-region-list">
            <div v-for="r in regionBreakdown" :key="r.name" class="pdm-region-row">
              <div class="pdm-region-dot" :style="`background:${r.color}`"></div>
              <div class="pdm-region-info">
                <div class="pdm-region-name">{{ r.name }}</div>
                <div class="pdm-region-bar-bg">
                  <div class="pdm-region-bar-fill" :style="`width:${r.pct}%;background:${r.color}`"></div>
                </div>
              </div>
              <div class="pdm-region-vals">
                <div class="pdm-region-total">{{ formatNum(r.value) }}</div>
                <div class="pdm-region-pct">{{ r.pct }}%</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const GEOJSON_URL =
  "https://raw.githubusercontent.com/apisit/thailand.json/master/thailand.json";

// GeoJSON นี้ระบุชื่อจังหวัดเป็นภาษาอังกฤษเท่านั้น (properties.name เช่น "Mae Hong Son")
// ในขณะที่ province_stats/rate_work_g จาก backend คืนชื่อจังหวัดเป็นภาษาไทยเสมอ — ถ้าไม่แปลง
// ให้ตรงกันก่อน การจับคู่จะไม่ตรงกันแม้แต่แถวเดียวทั้งประเทศ ทำให้แผนที่ขึ้นสีเดียวกันหมด
// (ไม่มีสี ไม่มีความต่าง) ทั้งที่ข้อมูลฝั่ง sidebar ถูกต้องสมบูรณ์ — ตรวจสอบชื่อจริงจากไฟล์
// GeoJSON โดยตรงแล้วจับคู่กับชื่อทางการ 77 จังหวัดในตาราง province ทีละรายการ
const ENGLISH_TO_THAI_PROVINCE = {
  'Amnat Charoen': 'อำนาจเจริญ',
  'Ang Thong': 'อ่างทอง',
  'Bangkok Metropolis': 'กรุงเทพมหานคร',
  'Bueng Kan': 'บึงกาฬ',
  'Buri Ram': 'บุรีรัมย์',
  'Chachoengsao': 'ฉะเชิงเทรา',
  'Chai Nat': 'ชัยนาท',
  'Chaiyaphum': 'ชัยภูมิ',
  'Chanthaburi': 'จันทบุรี',
  'Chiang Mai': 'เชียงใหม่',
  'Chiang Rai': 'เชียงราย',
  'Chon Buri': 'ชลบุรี',
  'Chumphon': 'ชุมพร',
  'Kalasin': 'กาฬสินธุ์',
  'Kamphaeng Phet': 'กำแพงเพชร',
  'Kanchanaburi': 'กาญจนบุรี',
  'Khon Kaen': 'ขอนแก่น',
  'Krabi': 'กระบี่',
  'Lampang': 'ลำปาง',
  'Lamphun': 'ลำพูน',
  'Loei': 'เลย',
  'Lop Buri': 'ลพบุรี',
  'Mae Hong Son': 'แม่ฮ่องสอน',
  'Maha Sarakham': 'มหาสารคาม',
  'Mukdahan': 'มุกดาหาร',
  'Nakhon Nayok': 'นครนายก',
  'Nakhon Pathom': 'นครปฐม',
  'Nakhon Phanom': 'นครพนม',
  'Nakhon Ratchasima': 'นครราชสีมา',
  'Nakhon Sawan': 'นครสวรรค์',
  'Nakhon Si Thammarat': 'นครศรีธรรมราช',
  'Nan': 'น่าน',
  'Narathiwat': 'นราธิวาส',
  'Nong Bua Lam Phu': 'หนองบัวลำภู',
  'Nong Khai': 'หนองคาย',
  'Nonthaburi': 'นนทบุรี',
  'Pathum Thani': 'ปทุมธานี',
  'Pattani': 'ปัตตานี',
  'Phangnga': 'พังงา',
  'Phatthalung': 'พัทลุง',
  'Phayao': 'พะเยา',
  'Phetchabun': 'เพชรบูรณ์',
  'Phetchaburi': 'เพชรบุรี',
  'Phichit': 'พิจิตร',
  'Phitsanulok': 'พิษณุโลก',
  'Phra Nakhon Si Ayutthaya': 'พระนครศรีอยุธยา',
  'Phrae': 'แพร่',
  'Phuket': 'ภูเก็ต',
  'Prachin Buri': 'ปราจีนบุรี',
  'Prachuap Khiri Khan': 'ประจวบคีรีขันธ์',
  'Ranong': 'ระนอง',
  'Ratchaburi': 'ราชบุรี',
  'Rayong': 'ระยอง',
  'Roi Et': 'ร้อยเอ็ด',
  'Sa Kaeo': 'สระแก้ว',
  'Sakon Nakhon': 'สกลนคร',
  'Samut Prakan': 'สมุทรปราการ',
  'Samut Sakhon': 'สมุทรสาคร',
  'Samut Songkhram': 'สมุทรสงคราม',
  'Saraburi': 'สระบุรี',
  'Satun': 'สตูล',
  'Si Sa Ket': 'ศรีสะเกษ',
  'Sing Buri': 'สิงห์บุรี',
  'Songkhla': 'สงขลา',
  'Sukhothai': 'สุโขทัย',
  'Suphan Buri': 'สุพรรณบุรี',
  'Surat Thani': 'สุราษฎร์ธานี',
  'Surin': 'สุรินทร์',
  'Tak': 'ตาก',
  'Trang': 'ตรัง',
  'Trat': 'ตราด',
  'Ubon Ratchathani': 'อุบลราชธานี',
  'Udon Thani': 'อุดรธานี',
  'Uthai Thani': 'อุทัยธานี',
  'Uttaradit': 'อุตรดิตถ์',
  'Yala': 'ยะลา',
  'Yasothon': 'ยโสธร'
}

const REGION_COLORS = {
  ภาคกลาง: '#455a64', ภาคเหนือ: '#2e7d32',
  ภาคตะวันออกเฉียงเหนือ: '#a86400', ภาคใต้: '#b3151b',
  ภาคตะวันออก: '#455a64', ภาคตะวันตก: '#b3151b',
  กรุงเทพมหานคร: '#b3151b'
}

// เกณฑ์อัตรากำลังมาตรฐาน (rate_work_g) มีเฉพาะ 3 ประเภทตำแหน่งนี้เท่านั้น — ประเภทอื่น
// (เจ้าพนักงานธุรการ/การเงินฯลฯ) ไม่มีสูตรเกณฑ์มาตรฐานในระบบ จึงแสดงได้เฉพาะความหนาแน่น
const CRITERION_FIELD_MAP = {
  'ผู้อำนวยการวิทยาลัย': { manpower: 'rate_work_g_dr', criterion: 'rate_criterion_dr', incomplete: 'rate_criterion_incomplete_dr' },
  'รองผู้อำนวยการวิทยาลัย': { manpower: 'rate_work_g_sedr', criterion: 'rate_criterion_sedr', incomplete: 'rate_criterion_incomplete_sedr' },
  'ครู': { manpower: 'rate_man_power_teach_all', criterion: 'rate_criterion_teach_all', incomplete: 'rate_criterion_incomplete_teach_all' }
}

export default {
  name: 'PositionDensityMap',

  props: {
    // rows จาก jor18.province_stats.php: { province_name, region_name, positions, total, vacant, occupied, colleges }
    provinceStats: { type: Array, default: () => [] },
    // rows ดิบจาก rate_work_g.php (ปีล่าสุด) — ต้องมี province_name จาก JOIN ฝั่ง backend แล้ว
    criterionRows: { type: Array, default: () => [] },
    criterionYear: { type: [String, Number], default: '' }
  },

  data() {
    return {
      selectedType: 'ครู',
      metric: 'density',
      colorScheme: 'blue',
      rankSearch: '',
      selectedProvince: null,
      leafletMap: null,
      leafletGeoLayer: null,
      geoData: null,
      resizeObserver: null,
      tooltip: { show: false, x: 0, y: 0, province: '', region: '', regionColor: '', total: 0, occupied: 0, vacant: 0, colleges: 0, manpower: 0, criterion: 0, incomplete: 0 },
      schemeOptions: [
        { label: 'น้ำเงิน', value: 'blue', color: '#b3151b' },
        { label: 'เขียว', value: 'green', color: '#263238' },
        { label: 'ส้ม', value: 'orange', color: '#a86400' }
      ]
    }
  },

  computed: {
    positionTypeOptions() {
      const map = {}
      this.provinceStats.forEach(r => {
        if (!map[r.positions]) map[r.positions] = { value: r.positions, total: 0 }
        map[r.positions].total += +r.total || 0
      })
      return Object.values(map).sort((a, b) => b.total - a.total)
    },

    selectedHasCriterion() {
      return !!CRITERION_FIELD_MAP[this.selectedType]
    },

    // { provinceName: { region, total, occupied, vacant, colleges } } เฉพาะประเภทที่เลือก
    densityIndex() {
      const idx = {}
      this.provinceStats
        .filter(r => r.positions === this.selectedType)
        .forEach(r => {
          idx[r.province_name] = {
            region: r.region_name || 'ไม่ระบุ',
            total: +r.total || 0,
            occupied: +r.occupied || 0,
            vacant: +r.vacant || 0,
            colleges: +r.colleges || 0
          }
        })
      return idx
    },

    // { provinceName: { manpower, criterion, incomplete } } เฉพาะประเภทที่มีเกณฑ์
    criterionIndex() {
      const fields = CRITERION_FIELD_MAP[this.selectedType]
      if (!fields) return {}
      const idx = {}
      this.criterionRows.forEach(r => {
        const pname = r.province_name
        if (!pname) return
        if (!idx[pname]) idx[pname] = { manpower: 0, criterion: 0, incomplete: 0 }
        idx[pname].manpower += +r[fields.manpower] || 0
        idx[pname].criterion += +r[fields.criterion] || 0
        idx[pname].incomplete += +r[fields.incomplete] || 0
      })
      return idx
    },

    typeTotal() { return Object.values(this.densityIndex).reduce((s, p) => s + p.total, 0) },
    typeOccupied() { return Object.values(this.densityIndex).reduce((s, p) => s + p.occupied, 0) },
    typeVacant() { return Object.values(this.densityIndex).reduce((s, p) => s + p.vacant, 0) },

    criterionManpowerTotal() { return Object.values(this.criterionIndex).reduce((s, p) => s + p.manpower, 0) },
    criterionQuotaTotal() { return Object.values(this.criterionIndex).reduce((s, p) => s + p.criterion, 0) },
    criterionIncompleteTotal() { return Object.values(this.criterionIndex).reduce((s, p) => s + p.incomplete, 0) },

    provinceRanking() {
      if (this.metric === 'density') {
        return Object.entries(this.densityIndex)
          .map(([name, d]) => ({ name, region: d.region, total: d.total, occupied: d.occupied, vacant: d.vacant, colleges: d.colleges }))
          .sort((a, b) => b.total - a.total)
      }
      if (!this.selectedHasCriterion) return []
      const regionOf = name => (this.densityIndex[name] && this.densityIndex[name].region) || 'ไม่ระบุ'
      return Object.entries(this.criterionIndex)
        .map(([name, d]) => ({ name, region: regionOf(name), manpower: d.manpower, criterion: d.criterion, incomplete: d.incomplete }))
        .sort((a, b) => a.incomplete - b.incomplete)
    },

    filteredRanking() {
      if (!this.rankSearch) return this.provinceRanking
      const q = this.rankSearch.toLowerCase()
      return this.provinceRanking.filter(p => p.name.toLowerCase().includes(q) || p.region.toLowerCase().includes(q))
    },

    maxTotal() {
      if (!this.provinceRanking.length) return 1
      if (this.metric === 'density') return Math.max(...this.provinceRanking.map(p => p.total)) || 1
      return Math.max(...this.provinceRanking.map(p => Math.abs(p.incomplete))) || 1
    },

    regionBreakdown() {
      const map = {}
      this.provinceRanking.forEach(p => {
        const r = p.region || 'ไม่ระบุ'
        if (!map[r]) map[r] = { name: r, value: 0 }
        map[r].value += this.metric === 'density' ? p.total : Math.abs(p.incomplete)
      })
      const grand = Object.values(map).reduce((s, r) => s + r.value, 0) || 1
      return Object.values(map).sort((a, b) => b.value - a.value).map(r => ({
        ...r, pct: Math.round((r.value / grand) * 100),
        color: REGION_COLORS[r.name] || '#6b7280'
      }))
    },

    legendTitle() {
      return this.metric === 'density' ? `จำนวนตำแหน่ง (${this.selectedType})` : `ขาด/เกินเกณฑ์ (${this.selectedType})`
    },
    legendMinLabel() { return this.metric === 'density' ? 'น้อย' : 'ขาดมาก' },
    legendMaxLabel() { return this.metric === 'density' ? 'มาก' : 'เกินมาก' },
    legendGradient() {
      if (this.metric === 'criterion') {
        return 'background:linear-gradient(90deg,#dc2626,#fca5a5,#f5f5f5,#81c784,#2e7d32);border-radius:4px'
      }
      const schemes = {
        blue: 'linear-gradient(to right, #e4e4e7, #b3151b)',
        green: 'linear-gradient(to right, #e8f5e9, #263238)',
        orange: 'linear-gradient(to right, #e6b04d, #a86400)'
      }
      return `background:${schemes[this.colorScheme] || schemes.blue};border-radius:4px`
    }
  },

  watch: {
    provinceStats() { this.refreshMapColors() },
    criterionRows() { this.refreshMapColors() },
    selectedType() { this.selectedProvince = null; this.refreshMapColors() },
    metric(val) {
      if (val === 'criterion' && !this.selectedHasCriterion) { this.metric = 'density'; return }
      this.selectedProvince = null
      this.refreshMapColors()
    },
    colorScheme() { this.refreshMapColors() }
  },

  mounted() {
    this.$nextTick(() => this.initLeafletMap())
  },

  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.leafletMap) {
      this.leafletMap.remove()
      this.leafletMap = null
    }
  },

  methods: {
    formatNum(n) {
      if (n === null || n === undefined) return '0'
      return (+n).toLocaleString('th-TH')
    },

    selectType(v) { this.selectedType = v },

    getProvValue(provName) {
      if (this.metric === 'density') {
        const d = this.densityIndex[provName]
        return d ? d.total : 0
      }
      const d = this.criterionIndex[provName]
      return d ? d.incomplete : 0
    },

    interpolateColor(t) {
      const schemes = {
        blue: { from: [219, 234, 254], to: [30, 64, 175] },
        green: { from: [209, 250, 229], to: [15, 118, 110] },
        orange: { from: [254, 215, 170], to: [180, 83, 9] }
      }
      const s = schemes[this.colorScheme] || schemes.blue
      const r = Math.round(s.from[0] + (s.to[0] - s.from[0]) * t)
      const g = Math.round(s.from[1] + (s.to[1] - s.from[1]) * t)
      const b = Math.round(s.from[2] + (s.to[2] - s.from[2]) * t)
      return `rgb(${r},${g},${b})`
    },

    // สีแบบ diverging สำหรับโหมดขาด/เกินเกณฑ์ (แดง=ขาด, เขียว=เกิน)
    divergingColor(incomplete) {
      const t = Math.max(-1, Math.min(1, incomplete / this.maxTotal))
      if (t < 0) {
        const r = Math.round(254 + (220 - 254) * -t)
        const g = Math.round(242 + (38 - 242) * -t)
        const b = Math.round(242 + (38 - 242) * -t)
        return `rgb(${r},${g},${b})`
      }
      const r = Math.round(240 + (22 - 240) * t)
      const g = Math.round(244 + (163 - 244) * t)
      const b = Math.round(250 + (74 - 250) * t)
      return `rgb(${r},${g},${b})`
    },

    onClickProvByName(name) {
      this.selectedProvince = this.selectedProvince === name ? null : name
    },

    getRankClass(i) {
      if (i === 0) return 'pdm-rank-num--gold'
      if (i === 1) return 'pdm-rank-num--silver'
      if (i === 2) return 'pdm-rank-num--bronze'
      return ''
    },

    getRegionColor(name) { return REGION_COLORS[name] || '#6b7280' },

    async initLeafletMap() {
      const el = this.$refs.mapEl
      if (!el || this.leafletMap) return
      this.leafletMap = L.map(el, {
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: true,
        minZoom: 5,
        maxZoom: 10
      })
      this.mapFit()

      // Leaflet คำนวณขนาด/พิกัดจาก DOM ตอนสร้างแผนที่ครั้งแรกเท่านั้น — ถ้าตอนนั้น container
      // ยังไม่มีขนาดจริง (เช่น layout ของ v-container/font ยังโหลดไม่เสร็จ ซึ่งมักเกิดเมื่อวาง
      // แผนที่ไว้ตอนต้นหน้าก่อนเนื้อหาหนักๆ อื่น) แผนที่จะกลายเป็นพื้นที่ 0x0 และไม่แสดงผลเลย
      // แม้ข้อมูลจะโหลดถูกต้องก็ตาม จึงต้องบังคับคำนวณขนาดใหม่หลายจุดกันเหตุการณ์นี้
      this.$nextTick(() => this.leafletMap && this.leafletMap.invalidateSize());
      setTimeout(() => this.leafletMap && this.leafletMap.invalidateSize(), 300);

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.leafletMap) this.leafletMap.invalidateSize();
        });
        this.resizeObserver.observe(el);
      }

      try {
        const res = await fetch(GEOJSON_URL)
        this.geoData = await res.json()
        this.renderGeoLayer()
        this.$nextTick(() => {
          if (this.leafletMap) {
            this.leafletMap.invalidateSize();
            this.mapFit();
          }
        });
      } catch (e) {
        console.error('GeoJSON load error:', e)
      }
    },

    renderGeoLayer() {
      if (!this.leafletMap || !this.geoData) return
      if (this.leafletGeoLayer) {
        this.leafletGeoLayer.remove()
        this.leafletGeoLayer = null
      }
      this.leafletGeoLayer = L.geoJSON(this.geoData, {
        style: f => this.geoStyle(f),
        onEachFeature: (f, layer) => this.bindLayerEvents(f, layer)
      }).addTo(this.leafletMap)
    },

    getFeatureName(feature) {
      const p = feature.properties || {}
      const raw = p.name || p.prov_nameTH || p.NAME_1 || p.province || ''
      // GeoJSON นี้ให้ชื่อจังหวัดเป็นภาษาอังกฤษ (เช่น "Mae Hong Son") ต้องแปลงเป็นไทยก่อนเสมอ
      // เพราะข้อมูลทั้งหมดที่ backend ส่งมา (province_stats/rate_work_g) คืนชื่อไทยเท่านั้น
      return ENGLISH_TO_THAI_PROVINCE[raw] || raw
    },

    geoStyle(feature) {
      const name = this.getFeatureName(feature)
      const isSelected = this.selectedProvince === name

      if (this.metric === 'criterion' && !this.selectedHasCriterion) {
        return { fillColor: '#e4e4e7', fillOpacity: 0.7, color: '#fff', weight: 0.8 }
      }

      const val = this.getProvValue(name)
      if (val === 0 && this.metric === 'density') {
        return { fillColor: '#f5f5f5', fillOpacity: 0.7, color: isSelected ? '#b3151b' : '#fff', weight: isSelected ? 1.8 : 0.8 }
      }

      const fillColor = this.metric === 'criterion'
        ? this.divergingColor(val)
        : this.interpolateColor(val / this.maxTotal)

      return { fillColor, fillOpacity: 0.85, color: isSelected ? '#b3151b' : '#fff', weight: isSelected ? 1.8 : 0.8 }
    },

    refreshMapColors() {
      if (!this.leafletGeoLayer) return
      this.leafletGeoLayer.eachLayer(layer => {
        if (layer.feature) layer.setStyle(this.geoStyle(layer.feature))
      })
    },

    bindLayerEvents(feature, layer) {
      layer.on({
        mousemove: e => {
          const name = this.getFeatureName(feature)
          const rect = this.$el.getBoundingClientRect()
          const d = this.densityIndex[name]
          const c = this.criterionIndex[name]
          this.tooltip = {
            show: true,
            x: e.originalEvent.clientX - rect.left + 12,
            y: e.originalEvent.clientY - rect.top - 10,
            province: name,
            region: (d && d.region) || '',
            regionColor: REGION_COLORS[(d && d.region)] || '#6b7280',
            total: d ? d.total : 0,
            occupied: d ? d.occupied : 0,
            vacant: d ? d.vacant : 0,
            colleges: d ? d.colleges : 0,
            manpower: c ? c.manpower : 0,
            criterion: c ? c.criterion : 0,
            incomplete: c ? c.incomplete : 0
          }
        },
        mouseout: () => { this.tooltip = { ...this.tooltip, show: false } },
        click: () => this.onClickProvByName(this.getFeatureName(feature))
      })
    },

    mapZoomIn() { this.leafletMap && this.leafletMap.zoomIn() },
    mapZoomOut() { this.leafletMap && this.leafletMap.zoomOut() },
    mapFit() {
      if (!this.leafletMap) return
      this.leafletMap.fitBounds([[5.5, 97.3], [20.5, 105.7]], { padding: [8, 8] })
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700;800&display=swap');

* { box-sizing: border-box; }
.pdm-wrap {
  font-family: 'Sarabun', sans-serif;
  background: #fff; border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,.08);
  overflow: visible; position: relative;
  border: 1px solid #e4e4e7;
}

.pdm-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid #f5f5f5;
  background: #263238;
  border-radius: 13px 13px 0 0;
}
.pdm-header-left { display: flex; align-items: center; gap: 12px; }
.pdm-icon-wrap { width: 36px; height: 36px; background: rgba(255,255,255,.2); border-radius: 8px; display: flex; align-items: center; justify-content: center; }
.pdm-title { font-size: 0.95rem; font-weight: 700; color: #fff; }
.pdm-subtitle { font-size: 0.68rem; color: rgba(255,255,255,.7); margin-top: 1px; }
.pdm-header-right { display: flex; align-items: center; gap: 6px; }

.pdm-controls {
  display: flex; align-items: flex-end; flex-wrap: wrap; gap: 14px;
  padding: 12px 18px; background: #f5f5f5; border-bottom: 1px solid #e4e4e7;
}
.pdm-filter-group { display: flex; flex-direction: column; gap: 4px; }
.pdm-filter-group--grow { flex: 1; min-width: 260px; }
.pdm-filter-label { font-size: 0.63rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .3px; }
.pdm-pill-group { display: flex; gap: 4px; }
.pdm-pill-group--wrap { flex-wrap: wrap; }
.pdm-pill { padding: 4px 10px; border-radius: 6px; font-size: 0.72rem; cursor: pointer; background: #f5f5f5; color: #64748b; transition: all .15s; display: flex; align-items: center; gap: 5px; font-weight: 500; }
.pdm-pill:hover { background: #fbecec; color: #b3151b; }
.pdm-pill--active { background: #455a64; color: #fff; }
.pdm-scheme-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

.pdm-no-criterion-note {
  padding: 8px 18px; font-size: 0.76rem; color: #a86400; background: #fff4dc;
  border-bottom: 1px solid #e6b04d;
}

.pdm-body { display: flex; gap: 0; min-height: 560px; }

.pdm-map-panel { flex: 1.4; position: relative; min-height: 560px; border-right: 1px solid #f5f5f5; background: #f5f5f5; overflow: hidden; display: flex; flex-direction: column; }

.pdm-map-title-bar { display: flex; align-items: center; padding: 8px 14px; background: #f5f5f5; border-bottom: 1px solid #e4e4e7; gap: 8px; z-index: 2; position: relative; }
.pdm-map-type-name { font-size: 0.82rem; font-weight: 700; color: #263238; white-space: nowrap; }
.pdm-map-stats { font-size: 0.68rem; color: #64748b; flex: 1; text-align: right; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pdm-zoom-btns { display: flex; gap: 4px; }
.pdm-zoom-btn { width: 24px; height: 24px; border: 1px solid #e4e4e7; background: #fff; border-radius: 5px; font-size: 0.9rem; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #374151; transition: background .1s; }
.pdm-zoom-btn:hover { background: #f5f5f5; color: #455a64; }

.pdm-leaflet-container { flex: 1; min-height: 0; position: relative; z-index: 1; background: #f5f5f5; }
.pdm-leaflet-container >>> .leaflet-container { background: #f5f5f5; font-family: 'Sarabun', sans-serif; }

.pdm-legend { padding: 8px 14px 10px; border-top: 1px solid #e4e4e7; background: #f5f5f5; }
.pdm-legend-title { font-size: 0.65rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .3px; margin-bottom: 5px; }
.pdm-legend-bar { display: flex; flex-direction: column; gap: 3px; }
.pdm-legend-gradient { height: 10px; width: 100%; border-radius: 5px; }
.pdm-legend-ticks { display: flex; justify-content: space-between; font-size: 0.65rem; color: #9ca3af; }

.pdm-tooltip {
  position: absolute; background: #263238; color: #fff;
  border-radius: 10px; padding: 10px 14px; font-size: 0.75rem;
  pointer-events: none; z-index: 300; min-width: 160px;
  box-shadow: 0 8px 24px rgba(0,0,0,.25);
}
.pdm-tt-header { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.pdm-tt-prov { font-weight: 700; font-size: 0.82rem; }
.pdm-tt-region { font-size: 0.62rem; padding: 1px 6px; border-radius: 999px; color: #fff; opacity: .9; }
.pdm-tt-body { display: flex; flex-direction: column; gap: 4px; }
.pdm-tt-row { display: flex; justify-content: space-between; gap: 16px; }
.pdm-tt-row span { color: rgba(255,255,255,.65); }
.pdm-tt-row strong { font-weight: 700; }

.pdm-stats-panel { width: 310px; flex-shrink: 0; display: flex; flex-direction: column; gap: 0; overflow-y: auto; max-height: 700px; }

.pdm-stat-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; padding: 12px; }
.pdm-stat-card { border-radius: 10px; padding: 10px 12px; }
.pdm-stat-card--blue { background: #f5f5f5; border: 1px solid #e4e4e7; }
.pdm-stat-card--teal { background: #e8f5e9; border: 1px solid #81c784; }
.pdm-stat-card--purple { background: #fbecec; border: 1px solid #fbecec; }
.pdm-stat-card--orange { background: #fff4dc; border: 1px solid #e6b04d; }
.pdm-stat-card--red { background: #fef2f2; border: 1px solid #fecaca; }
.pdm-stat-val { font-size: 1.3rem; font-weight: 800; color: #263238; line-height: 1.1; }
.pdm-stat-lbl { font-size: 0.65rem; color: #64748b; font-weight: 600; margin-top: 2px; }

.pdm-rank-panel, .pdm-region-panel { border-top: 1px solid #f5f5f5; }
.pdm-rank-header { display: flex; align-items: center; padding: 9px 14px; font-size: 0.78rem; font-weight: 700; color: #263238; background: #f5f5f5; border-bottom: 1px solid #f5f5f5; }
.pdm-rank-count { font-size: 0.68rem; color: #64748b; }
.pdm-rank-search { padding: 8px 12px; border-bottom: 1px solid #f5f5f5; }
.pdm-input { width: 100%; padding: 7px 10px; border: 1.5px solid #e4e4e7; border-radius: 8px; font-family: 'Sarabun', sans-serif; font-size: 0.82rem; outline: none; background: #fff; transition: border-color .15s; }
.pdm-input:focus { border-color: #455a64; box-shadow: 0 0 0 3px rgba(59,130,246,.12); }
.pdm-input--sm { padding: 5px 10px; font-size: 0.76rem; }
.pdm-rank-list { max-height: 320px; overflow-y: auto; }
.pdm-rank-row { display: flex; align-items: center; gap: 8px; padding: 7px 12px; cursor: pointer; transition: background .1s; }
.pdm-rank-row:hover { background: #f5f5f5; }
.pdm-rank-row--selected { background: #f5f5f5 !important; }
.pdm-rank-num { width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.62rem; font-weight: 700; flex-shrink: 0; background: #f5f5f5; color: #64748b; }
.pdm-rank-num--gold { background: #fff4dc; color: #a86400; }
.pdm-rank-num--silver { background: #f5f5f5; color: #475569; }
.pdm-rank-num--bronze { background: #fff4dc; color: #a86400; }
.pdm-rank-info { flex: 1; min-width: 0; }
.pdm-rank-name { font-size: 0.76rem; font-weight: 600; color: #263238; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pdm-rank-region { font-size: 0.63rem; font-weight: 600; }
.pdm-rank-val { font-size: 0.75rem; font-weight: 700; color: #263238; text-align: right; white-space: nowrap; }

.pdm-region-list { padding: 8px 12px; display: flex; flex-direction: column; gap: 6px; }
.pdm-region-row { display: flex; align-items: center; gap: 8px; }
.pdm-region-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.pdm-region-info { flex: 1; min-width: 0; }
.pdm-region-name { font-size: 0.73rem; font-weight: 600; color: #263238; }
.pdm-region-bar-bg { height: 5px; background: #e5e7eb; border-radius: 3px; overflow: hidden; margin-top: 2px; }
.pdm-region-bar-fill { height: 5px; border-radius: 3px; transition: width .4s; }
.pdm-region-vals { text-align: right; }
.pdm-region-total { font-size: 0.73rem; font-weight: 700; color: #263238; }
.pdm-region-pct { font-size: 0.63rem; color: #9ca3af; }

.tc-blue { color: #455a64 !important; }
.tc-orange { color: #a86400 !important; }
.tc-red { color: #dc2626 !important; }
.tc-green { color: #2e7d32 !important; }

@media (max-width: 820px) {
  .pdm-body { flex-direction: column; }
  .pdm-stats-panel { width: 100%; max-height: none; border-top: 1px solid #e4e4e7; }
  .pdm-map-panel { min-height: 420px; }
}
</style>
